import { LegalTypeEnum } from '@@fire-b2b/types/legal/LegalTypeEnum'
import { OrderTypeEnum } from '@@fire-b2b/types/order/OrderTypeEnum'
import type { WatchHandle } from 'vue'
import type { RouteLocationGeneric, RouteParamsGeneric } from 'vue-router'

/**
 * When a route parameter is optional, this value is used as a placeholder.
 * This value is treated as if the parameter was not provided/missing.
 */
export const OPTIONAL_PARAM_DEFAULT_VALUE = '-'

export type RouteParamType = 'id' | 'slug' | 'string' | 'enum'

export interface ValidRouteParam<V> {
  type: Readonly<RouteParamType>
  name: Readonly<string>
  exists: Readonly<true>
  status: Readonly<'valid'>
  value: Readonly<V>
}

export interface InvalidRouteParam<V> {
  type: Readonly<RouteParamType>
  name: Readonly<string>
  exists: Readonly<true>
  status: Readonly<'invalid'>
  value: Readonly<V>
}

export interface MissingRouteParam {
  type: Readonly<RouteParamType>
  name: Readonly<string>
  exists: Readonly<false>
  status: Readonly<'invalid'>
  value: Readonly<undefined>
}

export type ParsedRouteParam<V> = ValidRouteParam<V> | InvalidRouteParam<V> | MissingRouteParam

/**
 * Retrieves all possible route parameters in a single object for easier access.
 * - Parses numeric parameters to numbers.
 * - Validate enum parameters against their valid values.
 * - Returns undefined for parameters not present in the current route.
 * - Centralized access allows for easier future modifications to parameter names, for example.
 */
export function useRouteParams(to?: RouteLocationGeneric) {
  const route = to ?? useRoute()

  return {
    brandId: parseIdParam({ name: 'brandId', param: route.params.brandIdAndSlug }),
    brandSlug: parseSlugParam({ name: 'brandSlug', param: route.params.brandIdAndSlug }),

    orderType: parseEnumParam<OrderTypeEnum>({
      name: 'orderType',
      param: route.params.orderType,
      validValues: Object.values(OrderTypeEnum),
    }),

    seasonId: parseIdParam({ name: 'seasonId', param: route.params.seasonIdAndSlug }),
    seasonSlug: parseSlugParam({ name: 'seasonSlug', param: route.params.seasonIdAndSlug }),

    productLineId: parseIdParam({ name: 'productLineId', param: route.params.productlineIdAndSlug }),
    productLineSlug: parseSlugParam({ name: 'productLineSlug', param: route.params.productlineIdAndSlug }),

    themeworldCategoryId: parseIdParam({
      name: 'themeworldCategoryId',
      param: route.params.themeworldCategoryIdAndSlug,
    }),
    themeworldCategorySlug: parseSlugParam({
      name: 'themeworldCategorySlug',
      param: route.params.themeworldCategoryIdAndSlug,
    }),

    themeworldId: parseIdParam({ name: 'themeworldId', param: route.params.themeworldIdAndSlug }),
    themeworldSlug: parseSlugParam({ name: 'themeworldSlug', param: route.params.themeworldIdAndSlug }),

    lookCategoryId: parseIdParam({ name: 'lookCategoryId', param: route.params.lookCategoryIdAndSlug }),
    lookCategorySlug: parseSlugParam({ name: 'lookCategorySlug', param: route.params.lookCategoryIdAndSlug }),

    lookId: parseIdParam({ name: 'lookId', param: route.params.lookIdAndSlug }),
    lookSlug: parseSlugParam({ name: 'lookSlug', param: route.params.lookIdAndSlug }),

    shopRootCategoryId: parseIdParam({ name: 'shopRootCategoryId', param: route.params.shopRootCategoryIdAndSlug }),
    shopRootCategorySlug: parseSlugParam({
      name: 'shopRootCategorySlug',
      param: route.params.shopRootCategoryIdAndSlug,
    }),
    shopCategoryId: parseIdParam({ name: 'shopCategoryId', param: route.params.shopCategoryIdAndSlug }),
    shopCategorySlug: parseSlugParam({ name: 'shopCategorySlug', param: route.params.shopCategoryIdAndSlug }),

    searchQuery: parseStringParam({ name: 'searchQuery', param: route.params.searchQuery }),

    productId: parseIdParam({ name: 'productId', param: route.params.productIdAndSlug }),
    productSlug: parseSlugParam({ name: 'productSlug', param: route.params.productIdAndSlug }),

    orderId: parseIdParam({ name: 'orderId', param: route.params.id }),

    wishlistId: parseIdParam({ name: 'wishlistId', param: route.params.wishlistId }),

    supportCategoryId: parseIdParam({ name: 'supportCategoryId', param: route.params.supportCategoryIdAndSlug }),
    supportCategorySlug: parseSlugParam({ name: 'supportCategorySlug', param: route.params.supportCategoryIdAndSlug }),

    supportDetailId: parseIdParam({ name: 'supportDetailId', param: route.params.supportDetailIdAndSlug }),
    supportDetailSlug: parseSlugParam({ name: 'supportDetailSlug', param: route.params.supportDetailIdAndSlug }),

    blogCategoryId: parseIdParam({ name: 'blogCategoryId', param: route.params.blogCategoryIdAndSlug }),
    blogCategorySlug: parseSlugParam({ name: 'blogCategorySlug', param: route.params.blogCategoryIdAndSlug }),

    blogDetailId: parseIdParam({ name: 'blogDetailId', param: route.params.blogDetailIdAndSlug }),
    blogDetailSlug: parseSlugParam({ name: 'blogDetailSlug', param: route.params.blogDetailIdAndSlug }),

    teamCategoryId: parseIdParam({ name: 'teamCategoryId', param: route.params.teamCategoryIdAndSlug }),
    teamCategorySlug: parseIdParam({ name: 'teamCategorySlug', param: route.params.teamCategoryIdAndSlug }),

    downloadCategoryId: parseIdParam({ name: 'downloadCategoryId', param: route.params.categoryIdAndSlug }),
    downloadCategorySlug: parseSlugParam({
      name: 'downloadCategorySlug',
      param: route.params.categoryIdAndSlug,
    }),

    downloadId: parseIdParam({ name: 'downloadId', param: route.params.downloadIdAndSlug }),
    downloadSlug: parseSlugParam({ name: 'downloadSlug', param: route.params.downloadIdAndSlug }),

    formsDetailIdent: parseStringParam({ name: 'formsDetailIdent', param: route.params.ident }),

    accountOrderHistoryId: parseIdParam({ name: 'accountOrderHistoryId', param: route.params.id }),

    legalIdent: parseEnumParam({
      name: 'legalIdent',
      param: route.params.legalIdent,
      validValues: Object.values(LegalTypeEnum),
    }),
  }
}
export type RouteParams = ReturnType<typeof useRouteParams>

export function useRouteParamsRef(): {
  routeParamsRef: Ref<RouteParams>
  handle: WatchHandle
} {
  const router = useRouter()
  const routeParamsRef = ref<RouteParams>(useRouteParams(router.currentRoute.value))

  const handle = watch(router.currentRoute, (newRoute) => {
    routeParamsRef.value = useRouteParams(newRoute)
  })

  return {
    routeParamsRef,
    handle,
  }
}

// Parsing
// --------------------------------------------------------------------------------
export type RawRouteParam = RouteParamsGeneric[string] | undefined

type ParseParamRequest = {
  name: string
  param: RawRouteParam
}

/**
 * Example Param Value: "123-product-slug"
 * Returns:             "product-slug"
 * @internal Not intended for direct use outside of useRouteParams. Use routeParamResult() to unwrap.
 */
export function parseSlugParam(request: ParseParamRequest): ParsedRouteParam<string> {
  const type: RouteParamType = 'slug'

  if (request.param === undefined || request.param === OPTIONAL_PARAM_DEFAULT_VALUE) {
    return missingParam(type, request.name)
  }
  const paramValue = extractParamValue(request.param)

  if (!paramValue) {
    return missingParam(type, request.name)
  }

  const hyphenIndex = paramValue.indexOf('-')
  if (hyphenIndex === -1) {
    return missingParam(type, request.name)
  }

  const slugPart = paramValue.substring(hyphenIndex + 1)
  if (!slugPart) {
    return missingParam(type, request.name)
  }

  return {
    type,
    name: request.name,
    exists: true,
    status: 'valid',
    value: slugPart,
  }
}

/**
 * Example Param Value: "123-product-slug"
 * Returns:             123
 * @internal Not intended for direct use outside of useRouteParams. Use routeParamResult() to unwrap.
 */
export function parseIdParam(request: ParseParamRequest): ParsedRouteParam<number> {
  const type: RouteParamType = 'id'

  if (request.param === undefined || request.param === OPTIONAL_PARAM_DEFAULT_VALUE) {
    return missingParam(type, request.name)
  }
  const paramValue = extractParamValue(request.param)

  // If paramValue is empty or undefined, it's invalid
  if (!paramValue) {
    return missingParam(type, request.name)
  }

  const hyphenIndex = paramValue.indexOf('-')
  if (hyphenIndex === -1) {
    // If there's no hyphen, ensure the entire param is a valid number
    const parsedValue = Number(paramValue)
    const isValid = !isNaN(parsedValue)

    return {
      type,
      name: request.name,
      exists: true,
      status: isValid ? 'valid' : 'invalid',
      value: parsedValue,
    }
  }

  // If there's a hyphen, ensure the part before the hyphen is a valid number
  const idPart = paramValue.substring(0, hyphenIndex)
  if (!idPart) {
    return missingParam(type, request.name)
  }

  const parsedValue = parseInt(idPart, 10)
  const isValid = !isNaN(parsedValue) && parsedValue > 0

  // If not valid, means the id part is missing
  if (!isValid) {
    return missingParam(type, request.name)
  }

  return {
    type,
    name: request.name,
    exists: true,
    status: 'valid',
    value: parsedValue,
  }
}

/**
 * @internal Not intended for direct use outside of useRouteParams. Use routeParamResult() to unwrap.
 */
export function parseStringParam(request: ParseParamRequest): ParsedRouteParam<string> {
  const type: RouteParamType = 'string'

  if (request.param === undefined || request.param === OPTIONAL_PARAM_DEFAULT_VALUE) {
    return missingParam(type, request.name)
  }
  const paramValue = extractParamValue(request.param)

  if (!paramValue) {
    return missingParam(type, request.name)
  }

  return {
    type,
    name: request.name,
    exists: true,
    status: 'valid',
    value: paramValue,
  }
}

type ParseEnumParamRequest<T> = ParseParamRequest & {
  validValues: T[]
}
/**
 * @internal Not intended for direct use outside of useRouteParams. Use routeParamResult() to unwrap.
 */
export function parseEnumParam<T extends string>(request: ParseEnumParamRequest<T>): ParsedRouteParam<T> {
  const type: RouteParamType = 'enum'

  if (request.param === undefined || request.param === OPTIONAL_PARAM_DEFAULT_VALUE) {
    return missingParam(type, request.name)
  }
  const paramValue = extractParamValue(request.param)
  let isValid = false

  for (const validValue of request.validValues) {
    if (paramValue?.toLowerCase() === validValue.toLowerCase()) {
      isValid = true
      break
    }
  }

  return {
    type,
    name: request.name,
    exists: true,
    status: isValid ? 'valid' : 'invalid',
    value: paramValue as unknown as NonNullable<T>,
  }
}

function extractParamValue(param: RawRouteParam): string | undefined {
  return Array.isArray(param) ? param[0] : param
}

function missingParam(type: RouteParamType, name: string): MissingRouteParam {
  return {
    type,
    name,
    exists: false,
    status: 'invalid',
    value: undefined,
  }
}

// Type Guards
// --------------------------------------------------------------------------------
type UnwrapParamOptions = {
  fatal?: boolean
}
type UnwrapOptionalParamOptions<T> = UnwrapParamOptions & {
  fallbackValue?: T
}

/**
 * Returns the value of a required route parameter.
 * If invalid or missing, throws a 400 Bad Request error.
 */
export function unwrapParam<V>(param: ParsedRouteParam<V>, options?: UnwrapParamOptions): V {
  if (param.exists && param.status === 'valid') {
    return param.value
  }

  if (param.type === 'enum') {
    throw routeErrorNotFound({
      debugMessage: `Tried to unwrap invalid or missing enum route parameter '${param.name}' with value '${param.value}'`,
      fatal: options?.fatal ?? true,
    })
  }

  throw routeErrorBadRequest({
    debugMessage: `Tried to unwrap invalid or missing route parameter '${param.name}' with value '${param.value}'`,
    fatal: options?.fatal ?? true,
  })
}

/**
 * Returns the value of an optional route parameter, or undefined if missing.
 * If invalid, throws a 400 Bad Request error.
 */
export function unwrapOptionalParam<V>(param: ParsedRouteParam<V>, options: { fallbackValue: V }): V

export function unwrapOptionalParam<V>(
  param: ParsedRouteParam<V>,
  options?: UnwrapOptionalParamOptions<V>,
): V | undefined

export function unwrapOptionalParam<V>(
  param: ParsedRouteParam<V>,
  options?: UnwrapOptionalParamOptions<V>,
): V | undefined {
  if (param.exists) {
    if (param.status === 'valid') {
      return param.value
    } else if (param.status === 'invalid') {
      if (param.type === 'enum') {
        throw routeErrorNotFound({
          debugMessage: `Tried to unwrap invalid optional enum route parameter '${param.name}' with value '${param.value}'`,
          fatal: options?.fatal ?? true,
        })
      }

      throw routeErrorBadRequest({
        debugMessage: `Tried to unwrap invalid optional route parameter '${param.name}' with value '${param.value}'`,
        fatal: options?.fatal ?? true,
      })
    }
  }

  return options?.fallbackValue
}

// Result based unwrapping
// --------------------------------------------------------------------------------
export type RouteParamError = {
  type: 'MissingParam' | 'InvalidParam'
}
export type RouteParamResult<V> = Result<V, RouteParamError>
export type RouteParamResultOptions<T> = {
  fallbackValue?: T
}

/**
 * Returns a Result containing the value of a required route parameter.
 * If invalid or missing, returns an appropriate error.
 * Similar to unwrapParam but without throwing an error.
 */
export function routeParamResult<V>(param: ParsedRouteParam<V>): RouteParamResult<V> {
  if (param.exists) {
    if (param.status === 'valid') {
      return ok(param.value)
    } else {
      return err({
        type: 'InvalidParam',
      })
    }
  }
  return err({
    type: 'MissingParam',
  })
}

/**
 * Returns a Result containing the value of an optional route parameter, or undefined if missing.
 * If invalid, returns an appropriate error.
 * Similar to unwrapOptionalParam but without throwing an error.
 */
export function optionalRouteParamResult<V>(
  param: ParsedRouteParam<V>,
  options: { fallbackValue: V },
): RouteParamResult<V>

export function optionalRouteParamResult<V>(
  param: ParsedRouteParam<V>,
  options?: RouteParamResultOptions<V>,
): RouteParamResult<V | undefined>

export function optionalRouteParamResult<V>(
  param: ParsedRouteParam<V>,
  options?: RouteParamResultOptions<V>,
): RouteParamResult<V | undefined> {
  if (param.exists) {
    if (param.status === 'valid') {
      return ok(param.value)
    } else {
      return err({
        type: 'InvalidParam',
      })
    }
  }
  return ok(options?.fallbackValue)
}
